import React, { useEffect, useMemo, useState } from 'react';
import { Globals } from 'utils';
import { HomeService } from 'services';
import { CloseIcon, UploadIcon, ContactCallIcon, ChatIcon } from 'assets/icons';
import { Button, FallbackImage, Input, Modal, PaperClip, Select, Textarea } from 'components';
import { RootState } from 'reducers';
import { useSelector } from 'react-redux';

const days = [
  { value: '30', label: '30 días' },
  { value: '60', label: '60 días' },
  { value: '90', label: '90 días' },
];

interface Props {
  visible: boolean
  onClose: () => void
  onSubmit: (form: any) => void
}

const AddClassifiedModal = ({ visible, onClose, onSubmit: onContinue }: Props) => {
  const { form, onChange, onReset, onSubmit, onUploadPhoto, rate, activeContactMethods } = useForm(onContinue);

  return (
    <Modal
      className="modal-create-classified"
      withoutClose
      visible={visible}
      onClose={() => {
        onClose();
        onReset();
      }}
    >
      <div className="container">
        <button
          className="close"
          onClick={() => {
            onClose();
            onReset();
          }}
        >
          <img src={CloseIcon} alt="Close" />
        </button>

        <p className="title">Nuevo clasificado</p>

        <p className="total">
          <span>Monto: </span>
          <span className="amount">{Globals.formatMiles(Number(rate?.amount || 0) * (Number(form.days || 30) / 30), true, '$')}</span>
        </p>

        <br />
        <small>Complete la siguiente información para comprar su espacio publicitario</small>

        <div className="form">
          <div>
            <label>Cédula</label>
            <PaperClip
              className="paperclip-image"
              value={form.document}
              onChange={onUploadPhoto}
            >
              <FallbackImage
                src={form.document?.dataURL || form.document_blob}
                placeholder={UploadIcon}
                className="selected-image"
                placeholderClassName="selected-image inactive"
                alt="Logo"
              />

              <span className="filename">
                {form.document?.dataURL ? form.document?.original_name : 'Subir documento'}
              </span>
            </PaperClip>
          </div>

          <Input
            label="Título de clasificado"
            color="white"
            name="title"
            value={form.title}
            onChange={e => onChange(e.target.value, 'title')}
          />

          <div>
            <Textarea
              label="Contenido del anuncio"
              rows={3}
              name="content"
              value={form.content}
              onChange={(e: any) => onChange(e.target.value, 'content')}
            />
            <small>Escriba el contenido de su anuncio <span>(máximo 200 caracteres).</span></small>
          </div>

          <Select
            className="select-bank"
            label="Seleccione tiempo a contratar"
            name="days"
            value={form.days}
            onChange={(e: any) => {
              onChange(e.target.value, 'days');
              //onChange((Number(rate?.amount) * (Number(e.target.value) / 30)), 'amount');
            }}
            options={days}
          />
        </div>
        {activeContactMethods.length > 0 && (
          <div className="contact-methods-section">
            <label>Método de contacto</label>
            <div className="contact-methods-container">
              {activeContactMethods.map((method: any) => (
                <div key={`contact-${method.id}`} className="contact-method-box">
                  <img src={method.icon} alt={method.label} className="contact-method-icon" />
                  <span className="contact-method-text">{method.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center">
          <Button className="btn-next" onClick={onSubmit}>
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const useForm = (onContinue: (form: any) => void) => {
  const INITIAL_FORM = {
    payment_method_id: '',
    classified_rate_id: '',
    title: '',
    content: '',
    days: '',
    seller_code: '',
    document: {
      dataURL: '',
      original_name: '',
    },
    document_blob: '',
    amount: 0,
  }
  const user = useSelector((state: RootState) => state.user);
  console.log('Usuario en useForm de AddClassifiedModal:', user);
  const [rate, setRate] = useState<any>();
  const [form, setForm] = useState(INITIAL_FORM);
  const [affiliateProfile, setAffiliateProfile] = useState<any>(null);
  const getSelects = async () => {
    try {
      const rateRes: any = await HomeService.affiliate.getClassifiedRates();
      const baseRate = rateRes.classified || rateRes;

      setRate(baseRate);
      onChange(baseRate?.id, 'classified_rate_id');
      // precio base inmutable:
      onChange(baseRate?.amount || 0, 'amount');
      const profilePayload = { user_id: user?.id };
      if (user?.id) {
        const profileRes = await HomeService.affiliate.getProfile(profilePayload);
        console.log('Perfil de afiliado obtenido:', profileRes);
        setAffiliateProfile(profileRes);
      }

    } catch (error) {
      console.error("Error cargando data de los clasificados", error);
    }
  }

  const activeContactMethods = useMemo(() => {
    let methodsToDisplay: { id: string; label: string, icon: any }[] = [];
    const rawMethods = affiliateProfile?.consultation?.contact_methods;

    if (rawMethods) {
      try {
        const parsedMethods = typeof rawMethods === 'string' ? JSON.parse(rawMethods) : rawMethods;

        if (Array.isArray(parsedMethods)) {
          if (parsedMethods.includes('2') || parsedMethods.includes(2)) {
            methodsToDisplay.push({ id: '2', label: 'Llamada', icon: ContactCallIcon });
          }
          if (parsedMethods.includes('3') || parsedMethods.includes(3)) {
            methodsToDisplay.push({ id: '3', label: 'Chat', icon: ChatIcon });
          }
        }
      } catch (error) {
        console.error("Error al parsear los métodos de contacto:", error);
      }
    }
    return methodsToDisplay;
  }, [affiliateProfile]);

  const onChange = <T extends keyof typeof form>(value: any, target: T) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const onReset = () => {
    setForm({
      ...INITIAL_FORM,
      classified_rate_id: rate?.id || rate?.classified?.id || '',
    });
  }

  const onUploadPhoto = (e: any) => {
    setForm(s => ({
      ...s,
      document: e.target,
      document_blob: e.target?.dataURL,
    }))
  }

  const isValidForm = (showError = true) => {
    const onError = (msg: string) => {
      if (showError) Globals.showError(msg);
      return false;
    }

    if (!form.classified_rate_id)
      return onError('Debes seleccionar un banner');

    if (!form.title)
      return onError('Debes indicar el título del anuncio');

    if (!form.document?.dataURL && !form.document_blob)
      return onError('Debes subir un documento');
    if (!form.content)
      return onError('Debes escribir el contenido del anuncio');
    if (!form.classified_rate_id)
      return onError('No se pudo obtener la tarifa del clasificado. Intente recargar la página.');
    const contentLength = form.content.length;
    if (contentLength > 200)
      return onError(`El contenido del anuncio debe tener un máximo de 200 caracteres (${contentLength} caracteres ingresados)`);

    if (!form.days)
      return onError('Debes seleccionar un tiempo');

    return true;
  }

  const onSubmit = () => {
    if (!isValidForm()) return;
    const amount = Number(rate?.amount || 0) * (Number(form.days) / 30);
    onChange(amount, 'amount');
    onContinue({ ...form, amount });
  }

  useEffect(() => {
    getSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    isValidForm,
    onChange,
    onReset,
    onSubmit,
    onUploadPhoto,
    rate,
    activeContactMethods,
  }
}

export default AddClassifiedModal